import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getToken, setToken, removeToken, getUserType, setUserType, getDisplayName, setDisplayName, removeDisplayName, removeSocietyAdminData, getSocietyAdminData, SocietyAdminData, UserType } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  userType: UserType | null;
  displayName: string | null;
  societyAdminData: SocietyAdminData | null;
  login: (token: string, userType: UserType, displayName?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!getToken());
  const [userType, setUserTypeState] = useState<UserType | null>(getUserType());
  const [displayName, setDisplayNameState] = useState<string | null>(getDisplayName());
  const [societyAdminData, setSocietyAdminDataState] = useState<SocietyAdminData | null>(getSocietyAdminData());

  useEffect(() => {
    setIsAuthenticated(!!getToken());
    setUserTypeState(getUserType());
    setDisplayNameState(getDisplayName());
    setSocietyAdminDataState(getSocietyAdminData());
  }, []);

  const login = (token: string, type: UserType, name?: string) => {
    setToken(token);
    setUserType(type);
    setUserTypeState(type);
    if (name != null && name !== '') {
      setDisplayName(name);
      setDisplayNameState(name);
    } else {
      removeDisplayName();
      setDisplayNameState(null);
    }
    setSocietyAdminDataState(getSocietyAdminData());
    setIsAuthenticated(true);
  };

  const logout = () => {
    removeToken();
    removeSocietyAdminData();
    setUserTypeState(null);
    setDisplayNameState(null);
    setSocietyAdminDataState(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userType, displayName, societyAdminData, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

